import React, { useState } from 'react';
import { MessageSquare, Reply, Edit, Trash2, CheckCircle } from 'lucide-react';
import { Button } from '../../../components/ui';
import { useTranslation } from 'react-i18next';
import { hasPermission } from '../../../utils/rbac';
import { formatCommentTime } from '../../../utils/mockCommentsData';
import './CommentCard.css';

export function CommentCard({ 
  comment, 
  currentUser, 
  onReply, 
  onEdit, 
  onDelete, 
  onResolve,
  level = 0 
}) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const isOwnComment = currentUser?.id === comment.author?.id || currentUser?.email === comment.author?.email;
  const canEdit = isOwnComment && !comment.resolved;
  const canDelete = isOwnComment || hasPermission(currentUser, 'DELETE_COMMENT');
  const canResolve = hasPermission(currentUser, 'RESOLVE_COMMENT') && !comment.resolved;

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (editText.trim() && onEdit) {
      onEdit(comment.id, editText.trim());
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditText(comment.text);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (window.confirm(t('reports.workspace.comments.confirmDelete'))) {
      onDelete?.(comment.id);
    }
  };

  const handleResolve = () => {
    onResolve?.(comment.id);
  };

  return (
    <div className={`comment-card ${level > 0 ? 'comment-card--reply' : ''} ${comment.resolved ? 'comment-card--resolved' : ''}`}>
      <div className="comment-card__author">
        <div className="comment-card__avatar">
          {comment.author?.avatar ? (
            <img src={comment.author.avatar} alt={comment.author.name} />
          ) : (
            <span className="comment-card__avatar-initials">
              {getInitials(comment.author?.name || 'U')}
            </span>
          )}
        </div>
        <div className="comment-card__author-info">
          <div className="comment-card__author-name">
            <span>{comment.author?.name || t('reports.workspace.fallback.unknown')}</span>
            {comment.author?.role && (
              <span className="comment-card__author-role">{comment.author.role}</span>
            )}
            <span className="comment-card__timestamp">
              {formatCommentTime(comment.timestamp || comment.createdAt)}
            </span>
          </div>
        </div>
      </div>

      <div className="comment-card__content">
        {isEditing ? (
          <div className="comment-card__edit">
            <textarea
              className="comment-card__edit-textarea"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={3}
            />
            <div className="comment-card__edit-actions">
              <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                {t('common.cancel')}
              </Button>
              <Button variant="default" size="sm" onClick={handleSaveEdit}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="comment-card__text">{comment.text}</div>
        )}
      </div>

      <div className="comment-card__actions">
        {onReply && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onReply(comment.id)}
            className="comment-card__action-btn"
          >
            <MessageSquare size={12} />
            {t('reports.workspace.comments.reply')}
          </Button>
        )}
        {canEdit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleEdit}
            className="comment-card__action-btn"
          >
            <Edit size={12} />
            {t('common.edit')}
          </Button>
        )}
        {canDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            className="comment-card__action-btn comment-card__action-btn--delete"
          >
            <Trash2 size={12} />
            {t('common.delete')}
          </Button>
        )}
        {canResolve && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResolve}
            className="comment-card__action-btn"
          >
            <CheckCircle size={12} />
            {t('reports.workspace.comments.resolve')}
          </Button>
        )}
      </div>

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="comment-card__replies">
          {comment.replies.map((reply) => (
            <CommentCard
              key={reply.id}
              comment={reply}
              currentUser={currentUser}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onResolve={onResolve}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

